// Trips. Trip list as a bento grid, a create form, and a per trip history
// map.
//
// The settings block that used to sit at the bottom of this screen moved to
// Profil in round 5, which is where a traveller now expects language,
// default privacy and sign out to live. Nothing was dropped, only rehomed.
//
// Round 6: the history map draws PINS ONLY. The route polyline is gone from
// every map surface in the product, so a trip is expressed by where its
// entries are plus the order they were captured in, which is the list under
// the map. The track itself is still ingested and still read, it just is not
// drawn as a line.
import { useEffect, useMemo, useState } from "react";
import {
  Icon,
  IconCheck,
  IconPin,
  IconPlus,
  IconRefresh,
  IconRoute,
} from "@traveldiary/ui";
import { MapView } from "../components/MapView";
import { EmptyState } from "../components/EmptyState";
import { apiClient } from "../lib/api";
import { formatDateShort, formatDistance, formatNumber } from "../lib/format";
import { navigate, type Route } from "../lib/router";
import { groupMomentsIntoPlaces } from "../lib/places";
import {
  TRANSPORT_VALUES,
  type Moment,
  type PublicUser,
  type TrackPoint,
  type Transport,
  type Trip,
} from "../lib/types";
import { useApp } from "../state/app";

export function TripsScreen({ route }: { route: Route }) {
  if (route.param) return <TripHistory tripId={route.param} />;
  return <TripList />;
}

function todayInput(): string {
  const now = new Date();
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function TripList() {
  const {
    t,
    locale,
    trips,
    tripsFailed,
    createTrip,
    refreshTrips,
    activeTripId,
    setActiveTripId,
    moments,
    sync,
  } = useApp();

  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState(todayInput);
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [transport, setTransport] = useState<Transport[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const stats = useMemo(() => {
    const days = new Set(moments.map((moment) => moment.capturedAt.slice(0, 10)));
    return { moments: moments.length, days: days.size };
  }, [moments]);

  function toggleTransport(mode: Transport): void {
    setTransport((current) =>
      current.includes(mode) ? current.filter((item) => item !== mode) : [...current, mode],
    );
  }

  async function submit(): Promise<void> {
    if (!title.trim()) return;
    setSaving(true);
    setFailed(false);
    try {
      const trip = await createTrip({
        title: title.trim(),
        startDate,
        endDate: endDate || null,
        description: description.trim() || null,
        transportModes: transport,
      });
      setActiveTripId(trip.id);
      setCreating(false);
      setTitle("");
      setEndDate("");
      setDescription("");
      setTransport([]);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="screen-pad">
      <div className="tile-grid tile-grid-stats">
        <Stat label={t("trips.statMoments")} value={formatNumber(stats.moments, locale)} />
        <Stat label={t("trips.statDays")} value={formatNumber(stats.days, locale)} />
        <Stat label={t("trips.title")} value={formatNumber(trips.length, locale)} />
        <Stat label={t("sync.pending")} value={formatNumber(sync.pending, locale)} />
      </div>

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setCreating((value) => !value)}
        >
          <Icon size={18} color="var(--color-primary-contrast)">
            <IconPlus />
          </Icon>
          {t("trips.create")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={() => void refreshTrips()}>
          <Icon size={18}>
            <IconRefresh />
          </Icon>
          {t("trips.refresh")}
        </button>
      </div>

      {tripsFailed ? <p className="field-note">{t("trips.loadFailed")}</p> : null}

      {creating ? (
        <section className="card">
          <h2 className="card-title">{t("trips.formTitle")}</h2>

          <label className="field">
            <span className="field-label">{t("trips.fieldTitle")}</span>
            <input
              className="input"
              value={title}
              placeholder={t("trips.fieldTitlePlaceholder")}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>

          <div className="field-pair">
            <label className="field-sub">
              <span className="field-label-sm">{t("trips.fieldStart")}</span>
              <input
                className="input"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </label>
            <label className="field-sub">
              <span className="field-label-sm">{t("trips.fieldEnd")}</span>
              <input
                className="input"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </label>
          </div>

          <label className="field">
            <span className="field-label">{t("trips.fieldDescription")}</span>
            <textarea
              className="input input-area"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>

          <div className="field">
            <span className="field-label">{t("trips.fieldTransport")}</span>
            <div className="chip-row">
              {TRANSPORT_VALUES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={transport.includes(mode) ? "chip chip-on" : "chip"}
                  aria-pressed={transport.includes(mode)}
                  onClick={() => toggleTransport(mode)}
                >
                  {t(`transport.${mode}`)}
                </button>
              ))}
            </div>
          </div>

          {failed ? <p className="field-note">{t("trips.loadFailed")}</p> : null}

          <div className="row-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !title.trim()}
              onClick={() => void submit()}
            >
              {saving ? t("capture.saving") : t("trips.save")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setCreating(false)}>
              {t("trips.cancel")}
            </button>
          </div>
        </section>
      ) : null}

      {trips.length === 0 ? (
        <EmptyState glyph={<IconRoute />} title={t("trips.empty")} hint={t("trips.emptyHint")} />
      ) : (
        <div className="trip-grid">
          {trips.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              active={trip.id === activeTripId}
              onActivate={() => setActiveTripId(trip.id)}
            />
          ))}
        </div>
      )}

    </div>
  );
}

function TripCard({
  trip,
  active,
  onActivate,
}: {
  trip: Trip;
  active: boolean;
  onActivate(): void;
}) {
  const { t, locale } = useApp();
  return (
    <article className="trip-card">
      <h2 className="trip-title">{trip.title}</h2>
      <p className="trip-dates">
        {formatDateShort(trip.startDate, locale)}
        {trip.endDate ? ` ${t("trips.until")} ${formatDateShort(trip.endDate, locale)}` : ""}
      </p>
      <p className="trip-desc">{trip.description ?? ""}</p>
      <div className="trip-foot">
        <button
          type="button"
          className={active ? "btn btn-quiet btn-on" : "btn btn-quiet"}
          onClick={onActivate}
          aria-pressed={active}
        >
          {active ? (
            <Icon size={16}>
              <IconCheck />
            </Icon>
          ) : null}
          {active ? t("trips.active") : t("trips.setActive")}
        </button>
        <button
          type="button"
          className="btn btn-quiet"
          onClick={() => navigate("trips", trip.id)}
        >
          {t("trips.history")}
        </button>
      </div>
    </article>
  );
}

function TripHistory({ tripId }: { tripId: string }) {
  const { t, locale, trips, authUser } = useApp();
  const [track, setTrack] = useState<TrackPoint[]>([]);
  const [entries, setEntries] = useState<Moment[]>([]);
  const [recenterNonce, setRecenterNonce] = useState(0);
  const trip = trips.find((item) => item.id === tripId) ?? null;

  useEffect(() => {
    let cancelled = false;
    // The track is still ingested and still read: only its drawn line is
    // gone. The numbers below are what it now expresses.
    void apiClient
      .listTrack(tripId)
      .then((points) => {
        if (cancelled) return;
        setTrack(points.slice().sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)));
      })
      .catch(() => setTrack([]));
    void apiClient
      .listMoments(tripId)
      .then((items) => {
        if (cancelled) return;
        setEntries(items.slice().sort((a, b) => a.capturedAt.localeCompare(b.capturedAt)));
        setRecenterNonce((value) => value + 1);
      })
      .catch(() => setEntries([]));
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  /**
   * The journey, expressed as PINS. Round 6 removed every polyline from every
   * map surface, so what a trip was is carried by where its entries are and
   * the order they were captured in, not by a drawn route.
   */
  const places = useMemo(
    () =>
      groupMomentsIntoPlaces(
        entries
          .filter((entry) => entry.lat !== null && entry.lng !== null)
          .map((entry) => ({ ...entry, author: authUser as PublicUser, tripTitle: trip?.title ?? null })),
      ),
    [entries, authUser, trip?.title],
  );

  const distance = useMemo(() => {
    let total = 0;
    for (let i = 1; i < track.length; i += 1) {
      const a = track[i - 1]!;
      const b = track[i]!;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
      total += 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
    }
    return total;
  }, [track]);

  const days = useMemo(
    () => new Set(entries.map((entry) => entry.capturedAt.slice(0, 10))).size,
    [entries],
  );

  return (
    <div className="screen-pad">
      {/* No in screen back control: the shell header owns the one back
          affordance for every destination screen, and two of them stacked
          is a second thing to learn for no extra ability. */}
      <h2 className="section-title">{trip?.title ?? t("trips.history")}</h2>
      <p className="section-sub">{t("trips.historyHint")}</p>

      <div className="history-map">
        <MapView
          basemap="street"
          dimension="2d"
          places={places}
          selfUserId={authUser?.id ?? null}
          fitToData
          user={null}
          follow={false}
          recenterNonce={recenterNonce}
          onError={() => {}}
        />
      </div>

      <div className="tile-grid tile-grid-stats">
        <Stat label={t("trips.statMoments")} value={formatNumber(entries.length, locale)} />
        <Stat label={t("home.statPlaces")} value={formatNumber(places.length, locale)} />
        <Stat label={t("trips.statDays")} value={formatNumber(days, locale)} />
        <Stat label={t("trips.statDistance")} value={formatDistance(distance, locale)} />
      </div>

      {entries.length > 0 ? (
        <section className="section">
          <h2 className="section-title">{t("itinerary.title")}</h2>
          <div className="who-list">
            {entries.map((entry) => (
              <div className="who-row who-row-static" key={entry.id}>
                <span className="who-mark">
                  <Icon size={18}>
                    <IconPin />
                  </Icon>
                </span>
                <span className="who-text">
                  <span className="who-name">{entry.placeName || t("explore.noPlace")}</span>
                  <span className="who-meta">{formatDateShort(entry.capturedAt, locale)}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
    </div>
  );
}
